// ThankYouPage.jsx

import React, { useEffect, useState } from "react";
import "../index.css";
import axios from "axios";
import { useLocation } from "react-router-dom";
import ValuationFrame from "./ValuationFrame";
import {
  trackFunnelAction,
  trackFunnelError,
  trackFunnelStepView,
} from "../utils/analytics";

const PRELIMINARY_REVIEW_PRICE_LABEL =
  process.env.REACT_APP_SKYWARD_PRELIMINARY_REVIEW_PRICE_LABEL ||
  "EUR 499 launch offer";

function buildDocumentSubmissionEmailUrl(docId) {
  const params = new URLSearchParams({
    subject: "Preliminary Valuation Review documents",
    body:
      `Valuation ID: ${docId || "N/A"}\n\n` +
      "Please find attached my valuation report, model, and supporting information for the Skyward Preliminary Valuation Review.",
  });

  return `mailto:admin@skywardinnovation.com?${params.toString()}`;
}

function hasConfiguredPreliminaryReviewPaymentUrl() {
  return Boolean(
    String(
      process.env.REACT_APP_SKYWARD_PRELIMINARY_REVIEW_PAYMENT_URL || "",
    ).trim(),
  );
}

function selectedServiceCount(services) {
  if (!services || typeof services !== "object") return 0;
  return Object.values(services).filter(Boolean).length;
}

function buildPreliminaryReviewPaymentUrl(docId) {
  const configuredUrl = String(
    process.env.REACT_APP_SKYWARD_PRELIMINARY_REVIEW_PAYMENT_URL || "",
  ).trim();
  if (!configuredUrl) {
    return "mailto:admin@skywardinnovation.com?subject=Preliminary%20Valuation%20Review";
  }

  try {
    const paymentUrl = new URL(configuredUrl);
    if (docId) paymentUrl.searchParams.set("client_reference_id", docId);
    paymentUrl.searchParams.set("utm_source", "thank_you_page");
    paymentUrl.searchParams.set("utm_medium", "conversion");
    paymentUrl.searchParams.set("utm_campaign", "preliminary_valuation_review");
    return paymentUrl.toString();
  } catch (error) {
    return "mailto:admin@skywardinnovation.com?subject=Preliminary%20Valuation%20Review";
  }
}

function buildServiceContactEmailUrl(serviceName) {
  const params = new URLSearchParams({
    subject: `Skyward follow-up request: ${serviceName}`,
    body:
      `I would like to discuss ${serviceName}.\n\n` +
      "Please contact me about this service.",
  });

  return `mailto:admin@skywardinnovation.com?${params.toString()}`;
}

function ThankYouScreen() {
  const location = useLocation();
  const docIdFromQuery = new URLSearchParams(location.search).get("docId");
  const followUpTokenFromQuery = new URLSearchParams(location.search).get(
    "followUpToken",
  );
  const [docId] = useState(location.state?.docId || docIdFromQuery || null);
  const [followUpToken] = useState(
    location.state?.followUpToken || followUpTokenFromQuery || "",
  );
  const [selectedServices, setSelectedServices] = useState({
    droneFootage: false,
    consulting: false,
    financeArranging: false,
    brokerConnection: false,
    energySustainability: false,
  });
  const [isSubmittingServiceRequest, setIsSubmittingServiceRequest] =
    useState(false);
  const [selectedDocumentFile, setSelectedDocumentFile] = useState(null);
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);
  const [documentUploadMessage, setDocumentUploadMessage] = useState("");
  const staticPaymentLinkConfigured =
    hasConfiguredPreliminaryReviewPaymentUrl();
  const paymentStatus = new URLSearchParams(location.search).get("payment");
  const isPaymentSuccessful = paymentStatus === "success";
  const preliminaryReviewPaymentUrl = buildPreliminaryReviewPaymentUrl(docId);
  const documentSubmissionEmailUrl = buildDocumentSubmissionEmailUrl(docId);
  const selectedDocumentCount = selectedDocumentFile?.length || 0;

  useEffect(() => {
    trackFunnelStepView(5, "thank_you", {
      payment_status: paymentStatus || "none",
    });

    if (paymentStatus === "success") {
      trackFunnelAction("stripe_payment_success_return", {
        payment_status: "success",
      });
    } else if (paymentStatus === "cancelled") {
      trackFunnelAction("stripe_payment_cancelled_return", {
        payment_status: "cancelled",
      });
    }
  }, [paymentStatus]);

  const handleToggle = (service) => {
    setSelectedServices((prev) => {
      const selected = !prev[service];
      trackFunnelAction("additional_service_toggle", {
        service_id: service,
        selected,
      });

      return {
        ...prev,
        [service]: selected,
      };
    });
  };

  const submitServiceRequest = async ({
    requestType = "general_follow_up",
    servicesToSubmit,
    requestNote = "",
    successMessage,
  }) => {
    try {
      if (isSubmittingServiceRequest) {
        return false;
      }

      setIsSubmittingServiceRequest(true);
      if (!docId) {
        trackFunnelError(5, "thank_you", "missing_doc_id", {
          request_type: requestType,
        });
        alert(
          "We could not link this request to your valuation. Please contact Skyward Innovation directly.",
        );
        return false;
      }

      if (!followUpToken) {
        trackFunnelError(5, "thank_you", "missing_follow_up_token", {
          request_type: requestType,
        });
        alert(
          "This follow-up link is missing its verification token. Please contact Skyward Innovation directly.",
        );
        return false;
      }

      const selected_count = selectedServiceCount(servicesToSubmit);
      const hasSelectedService = selected_count > 0;
      if (!hasSelectedService) {
        trackFunnelError(5, "thank_you", "no_service_selected", {
          request_type: requestType,
          selected_service_count: selected_count,
        });
        alert("Please select at least one service before submitting.");
        return false;
      }

      trackFunnelAction("follow_up_request_attempt", {
        request_type: requestType,
        selected_service_count: selected_count,
      });

      await axios.post("/api/services", {
        docId,
        followUpToken,
        selectedServices: servicesToSubmit,
        requestType,
        requestNote,
      });

      trackFunnelAction("follow_up_request_success", {
        request_type: requestType,
        selected_service_count: selected_count,
      });
      alert(successMessage);
      return true;
    } catch (error) {
      trackFunnelError(5, "thank_you", "follow_up_request_failed", {
        request_type: requestType,
        selected_service_count: selectedServiceCount(servicesToSubmit),
      });
      const serverMessage =
        error.response?.data?.message || error.response?.data?.errors?.[0]?.msg;
      alert(
        serverMessage ||
          "Oops, something went wrong while submitting your request.",
      );
      return false;
    } finally {
      setIsSubmittingServiceRequest(false);
    }
  };

  const handlePaymentRequest = async () => {
    trackFunnelAction("preliminary_review_cta_click", {
      static_payment_link_configured: staticPaymentLinkConfigured,
    });

    const servicesToSubmit = {
      ...selectedServices,
      consulting: true,
    };

    if (staticPaymentLinkConfigured) {
      trackFunnelAction("stripe_payment_link_opened", {
        static_payment_link_configured: true,
      });
      setSelectedServices(servicesToSubmit);
      window.open(preliminaryReviewPaymentUrl, "_blank", "noopener,noreferrer");
      return;
    }

    if (!docId || !followUpToken) {
      await submitServiceRequest({
        requestType: "preliminary_review_payment_link",
        servicesToSubmit,
        requestNote:
          "Please send a secure payment link for the Preliminary Valuation Review.",
        successMessage:
          "Thanks. We have recorded your request and will send payment instructions.",
      });
      return;
    }

    try {
      if (isSubmittingServiceRequest) {
        return;
      }

      setIsSubmittingServiceRequest(true);
      const response = await axios.post(
        "/api/preliminary-review/checkout-session",
        {
          docId,
          followUpToken,
        },
      );

      if (response.data?.checkoutUrl) {
        trackFunnelAction("stripe_checkout_redirect", {
          static_payment_link_configured: false,
          selected_service_count: selectedServiceCount(servicesToSubmit),
        });
        setSelectedServices(servicesToSubmit);
        window.location.href = response.data.checkoutUrl;
        return;
      }

      trackFunnelAction("stripe_checkout_manual_fallback", {
        selected_service_count: selectedServiceCount(servicesToSubmit),
      });
    } catch (error) {
      trackFunnelError(5, "thank_you", "stripe_checkout_unavailable");
      console.warn(
        "Stripe checkout unavailable; falling back to payment-link request.",
        error.response?.data?.message || error.message,
      );
    } finally {
      setIsSubmittingServiceRequest(false);
    }

    const submitted = await submitServiceRequest({
      requestType: "preliminary_review_payment_link",
      servicesToSubmit,
      requestNote:
        "Please send a secure payment link for the Preliminary Valuation Review.",
      successMessage:
        "Thanks. We have recorded your request and will send payment instructions.",
    });

    if (submitted) {
      setSelectedServices(servicesToSubmit);
    }
  };

  const handleDocumentUpload = async () => {
    const selectedFiles = Array.from(selectedDocumentFile || []);
    if (!selectedFiles.length) {
      setDocumentUploadMessage(
        "Please choose one or more PDF, Excel, CSV, Word, JPG, or PNG files.",
      );
      return;
    }

    if (!docId || !followUpToken) {
      setDocumentUploadMessage(
        "This upload link is missing its verification details. Please email admin@skywardinnovation.com.",
      );
      return;
    }

    try {
      setIsUploadingDocument(true);
      setDocumentUploadMessage(
        selectedFiles.length === 1
          ? "Uploading document..."
          : `Uploading ${selectedFiles.length} documents...`,
      );
      trackFunnelAction("paid_review_document_upload_attempt", {
        file_count: selectedFiles.length,
      });

      for (const [index, file] of selectedFiles.entries()) {
        setDocumentUploadMessage(
          `Uploading ${index + 1} of ${selectedFiles.length}: ${
            file.name || "document"
          }`,
        );
        await axios.post(
          `/api/preliminary-review/documents?docId=${encodeURIComponent(
            docId,
          )}&followUpToken=${encodeURIComponent(followUpToken)}`,
          file,
          {
            headers: {
              "Content-Type": file.type || "application/octet-stream",
              "X-Skyward-File-Name": encodeURIComponent(
                file.name || "valuation-document",
              ),
            },
          },
        );
      }

      trackFunnelAction("paid_review_document_upload_success", {
        file_count: selectedFiles.length,
      });
      setDocumentUploadMessage(
        selectedFiles.length === 1
          ? "Document uploaded. Skyward will review it against your valuation."
          : `${selectedFiles.length} documents uploaded. Skyward will review them against your valuation.`,
      );
      setSelectedDocumentFile(null);
    } catch (error) {
      const serverMessage =
        error.response?.data?.message ||
        "Document upload failed. Please try again or email admin@skywardinnovation.com.";
      trackFunnelError(5, "thank_you", "paid_review_document_upload_failed", {
        status: error.response?.status || "unknown",
      });
      setDocumentUploadMessage(serverMessage);
    } finally {
      setIsUploadingDocument(false);
    }
  };

  const handleSubmit = async () => {
    const submitted = await submitServiceRequest({
      requestType: "general_follow_up",
      servicesToSubmit: selectedServices,
      successMessage:
        "Thanks. Your follow-up request has been submitted successfully.",
    });

    if (submitted) {
      setSelectedServices(selectedServices);
    }
  };

  const services = [
    {
      id: "consulting",
      name: "Valuation & Advisory / M&A",
      description:
        "Discuss valuation, sale, acquisition, refinance, or strategic advisory needs.",
    },
    {
      id: "droneFootage",
      name: "Drone Footage",
      description:
        "Capture aerial views to support property presentation or development progress updates.",
    },
    {
      id: "financeArranging",
      name: "Finance / Loan Arranging",
      description:
        "Discuss potential financing routes for purchase, refinance, or capital improvement planning.",
    },
    {
      id: "energySustainability",
      name: "Energy & Sustainability Upgrades",
      description:
        "Review solar, energy-efficiency, and sustainability opportunities that may reduce costs and support valuation or refinancing discussions.",
    },
    {
      id: "brokerConnection",
      name: "Connect to a Hotel Broker",
      description:
        "Speak with experienced hotel brokers about buying, selling, or market positioning.",
    },
  ];

  return (
    <ValuationFrame
      currentStep={5}
      title={isPaymentSuccessful ? "Payment Received" : "Valuation Submitted"}
      subtitle={
        isPaymentSuccessful
          ? "Upload your valuation report, model, and supporting information so Skyward can start the review."
          : "Your preliminary valuation range will be sent by email."
      }
    >
      {isPaymentSuccessful && (
        <section className="result-panel payment-next-step">
          <div className="review-progress" aria-label="Review progress">
            <div className="review-progress-step complete">
              <span>1</span>
              <strong>Submission received</strong>
            </div>
            <div className="review-progress-step complete">
              <span>2</span>
              <strong>Payment received</strong>
            </div>
            <div className="review-progress-step active">
              <span>3</span>
              <strong>Upload documents</strong>
            </div>
          </div>

          <p className="result-label">Next Step</p>
          <p className="result-value">Upload Your Documents</p>
          <p className="result-meta">
            Upload your valuation report, financial model or Excel workbook, and
            any other supporting information. Files are attached to your paid
            review ID for Skyward internal analysis.
          </p>
          <ul className="document-list">
            <li>Current valuation report or broker opinion, if available</li>
            <li>Excel model, operating statement, or latest trading summary</li>
            <li>
              Any capex, rent, financing, or ownership notes you want reviewed
            </li>
          </ul>
          <div className="upload-panel">
            <input
              type="file"
              multiple
              accept=".pdf,.xls,.xlsx,.csv,.doc,.docx,.jpg,.jpeg,.png,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png"
              onChange={(event) => {
                setSelectedDocumentFile(
                  event.target.files?.length ? event.target.files : null,
                );
                setDocumentUploadMessage("");
              }}
              disabled={isUploadingDocument}
            />
            <button
              type="button"
              className="button"
              onClick={handleDocumentUpload}
              disabled={isUploadingDocument || selectedDocumentCount === 0}
            >
              {isUploadingDocument ? "Uploading..." : "Upload Documents"}
            </button>
          </div>
          <p className="helper">
            Files are stored in a private quarantine for review. Automated
            virus scanning and OCR extraction are being added before documents
            can be used in the analyst pack.
          </p>
          {documentUploadMessage && (
            <p className="helper">{documentUploadMessage}</p>
          )}
          <p className="helper">
            If upload gives you trouble, you can still email files to
            admin@skywardinnovation.com with your valuation ID.
          </p>
          <a
            href={documentSubmissionEmailUrl}
            className="button secondary-button"
            onClick={() => {
              trackFunnelAction("document_email_click", {
                source: "payment_success_fallback",
              });
            }}
          >
            Email Instead
          </a>
        </section>
      )}

      {paymentStatus === "cancelled" && (
        <section className="result-panel">
          <p className="result-label">Payment Status</p>
          <p className="result-value">Not Completed</p>
          <p className="result-meta">
            You can restart the Preliminary Valuation Review when ready.
          </p>
        </section>
      )}

      {!isPaymentSuccessful && (
        <section className="result-panel">
          <p className="result-label">Submission Status</p>
          <p className="result-value">Received</p>
          <p className="result-meta">
            The valuation is for discussion purposes only and should not be
            relied upon as a formal appraisal. Valuation output is in the
            currency of the data entered.
          </p>
        </section>
      )}

      {!isPaymentSuccessful && (
        <section
          className="review-offer"
          aria-label="Preliminary valuation review launch offer"
        >
          <p className="review-offer-eyebrow">Special launch offer</p>
          <div className="review-offer-header">
            <div>
              <h2>Preliminary Valuation Review</h2>
              <p>
                Turn your model output into a reviewed discussion pack covering
                assumptions, value drivers, key risk flags, and next-step notes.
                If an existing valuation is below your Skyward range, we can
                help review the assumptions and questions to raise.
              </p>
            </div>
            <p className="review-offer-price">
              {PRELIMINARY_REVIEW_PRICE_LABEL}
            </p>
          </div>
          <ul className="review-offer-list">
            <li>Reviewed assumptions and value drivers</li>
            <li>Key risk flags and sensitivity comments</li>
            <li>
              Discussion pack before lender, broker, or adviser conversations
            </li>
          </ul>
          <p className="helper">
            After checkout, Skyward will show instructions for emailing
            valuation PDFs, Excel workbooks, or supporting files.
          </p>
          <div className="review-offer-actions">
            <button
              type="button"
              onClick={handlePaymentRequest}
              className="button"
              disabled={isSubmittingServiceRequest}
            >
              Pay Now
            </button>
          </div>
        </section>
      )}

      <section className="table-panel">
        <h2 className="fieldset-title">Can we help you with anything else?</h2>
        <p className="helper">
          Select any areas where you would like Skyward Innovation or one of our
          partners to reach out.
        </p>
        <div className="table-wrap">
          <table className="services-table">
            <thead>
              <tr>
                <th>Contact Me</th>
                <th>Service</th>
                <th>Description</th>
                <th>Contact</th>
              </tr>
            </thead>
            <tbody>
              {services.map((service) => (
                <tr key={service.id}>
                  <td>
                    <label className="service-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedServices[service.id]}
                        onChange={() => handleToggle(service.id)}
                        aria-label={`Contact me about ${service.name}`}
                      />
                    </label>
                  </td>
                  <td>{service.name}</td>
                  <td>{service.description}</td>
                  <td>
                    <a
                      href={buildServiceContactEmailUrl(service.name)}
                      onClick={() => {
                        trackFunnelAction("additional_service_link_click", {
                          service_id: service.id,
                        });
                      }}
                    >
                      Contact
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="button-row">
          <button
            type="button"
            onClick={handleSubmit}
            className="button"
            disabled={isSubmittingServiceRequest}
          >
            Request Follow-Up
          </button>
        </div>
      </section>

      <p className="helper">
        The valuation results are intended for informational and discussion
        purposes only. They do not constitute professional, financial, legal, or
        tax advice, nor should they be relied upon as an official appraisal.
        Please consult your own professional advisors before making financial or
        investment decisions.
      </p>

      <a href="/" className="button secondary-button">
        Return to Home
      </a>
    </ValuationFrame>
  );
}

export default ThankYouScreen;
